/**
 * Empty state hero for the Rules page.
 *
 * Shown when the active tab has zero rules. Content varies by tab
 * so Bank rules and Integration rules read naturally for the
 * actual thing a user is about to set up.
 *
 * Layout:
 *   - Vela sort animation (reusable VelaSortAnimation component)
 *   - Heading
 *   - One-paragraph explanation with a concrete example
 *   - Primary CTA (Create your first rule) → opens Rule builder
 *     (stubbed with toast in PR 9)
 *   - "How it works" 3-step section below a soft divider
 *
 * Follows the Novala standing rule for new-user empty states:
 *   1. Explain the value proposition in one sentence
 *   2. Show a clear primary CTA to add the data
 *   3. Teach how it works (3 numbered steps)
 */

import { useEffect, useState, type CSSProperties } from "react";
import type { RuleTab } from "../rulesTypes";
import { VelaSortAnimation } from "../../../../components/VelaSortAnimation";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  lineSoft: "#E7EAF0",
  vela: "#6B46C1",
};

const TOAST_DURATION_MS = 3200;

interface RulesEmptyStateProps {
  tab: RuleTab;
}

interface TabContent {
  tagLabels: [string, string, string];
  heading: string;
  description: string;
  ctaLabel: string;
  stepsHeading: string;
  steps: Array<{ title: string; desc: string; withVela?: boolean }>;
}

const CONTENT: Record<RuleTab, TabContent> = {
  bank: {
    tagLabels: ["OFFICE", "SALES", "TRAVEL"],
    heading: "Teach Vela your bookkeeping shortcuts",
    description:
      'A bank rule tells Vela how to handle a specific kind of bank transaction. Set one up like "Costco purchases go to Office supplies" and every future Costco charge is categorized the moment it imports.',
    ctaLabel: "Create your first bank rule",
    stepsHeading: "How bank rules work",
    steps: [
      {
        title: "Describe the match",
        desc:
          "Pick conditions: when payee contains text, when amount falls in a range, when a specific bank account is used, or combine several.",
      },
      {
        title: "Set what happens",
        desc:
          "Choose a category and account, add class or location, flag reimbursable or billable, add a memo.",
      },
      {
        title: "Vela applies it forever",
        desc:
          "Every new bank transaction is checked against your rules the moment it imports. Edit, pause, or delete anytime.",
        withVela: true,
      },
    ],
  },
  integration: {
    tagLabels: ["ONLINE", "PAYOUT", "TIPS"],
    heading: "Rules for your sales channels",
    description:
      'Integration rules handle transactions from Stripe, Shopify, Square, Amazon, and other connected sources. Set a rule like "Shopify orders go to Online sales" and every order is categorized the moment it syncs.',
    ctaLabel: "Create your first integration rule",
    stepsHeading: "How integration rules work",
    steps: [
      {
        title: "Pick a source and conditions",
        desc:
          "Stripe payment method, Shopify order tag, Square product category, Amazon marketplace fee type, or combine several conditions across one source.",
      },
      {
        title: "Set what happens",
        desc:
          "Category, revenue account, deposit account, class or location, GST treatment, memo.",
      },
      {
        title: "Vela applies it the moment orders sync",
        desc:
          "Every new order, payout, or fee from your integrations is matched and posted automatically.",
        withVela: true,
      },
    ],
  },
};

export function RulesEmptyState({ tab }: RulesEmptyStateProps) {
  const { isMobile } = useBreakpoint();
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), TOAST_DURATION_MS);
    return () => window.clearTimeout(t);
  }, [toast]);

  const content = CONTENT[tab];

  const handleCreateFirstRule = () => {
    setToast("Rule builder is coming in the next release.");
  };

  return (
    <div style={wrapStyle}>
      <div style={isMobile ? heroStyleMobile : heroStyle}>
        <VelaSortAnimation
          width={isMobile ? 150 : 180}
          tagLabels={content.tagLabels}
        />
        <h2 style={isMobile ? headingStyleMobile : headingStyle}>
          {content.heading}
        </h2>
        <p style={isMobile ? descStyleMobile : descStyle}>
          {content.description}
        </p>
        <button type="button" style={ctaBtnStyle} onClick={handleCreateFirstRule}>
          {content.ctaLabel}
        </button>
      </div>

      <div style={isMobile ? howSectionMobile : howSection}>
        <div style={howLabelStyle}>{content.stepsHeading}</div>
        {content.steps.map((step, idx) => (
          <div key={idx} style={isMobile ? howStepMobile : howStep}>
            <div style={isMobile ? stepNumMobile : stepNum}>{idx + 1}</div>
            <div>
              <div style={stepTitleStyle}>
                {step.withVela && <VMark />}
                {step.title}
              </div>
              <div style={stepDescStyle}>{step.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {toast && <Toast message={toast} />}
    </div>
  );
}

function VMark() {
  return <span style={vMarkStyle}>V</span>;
}

function Toast({ message }: { message: string }) {
  return (
    <div style={toastStyle} role="status" aria-live="polite">
      {message}
    </div>
  );
}

const wrapStyle: CSSProperties = {
  width: "100%",
  background: "#ffffff",
};

const heroStyle: CSSProperties = {
  textAlign: "center",
  padding: "48px 32px 32px",
  maxWidth: 540,
  margin: "0 auto",
};

const heroStyleMobile: CSSProperties = {
  textAlign: "center",
  padding: "28px 16px 20px",
  maxWidth: "100%",
  margin: "0 auto",
};

const headingStyle: CSSProperties = {
  fontSize: 24,
  fontWeight: 700,
  margin: "14px 0 10px",
  letterSpacing: "-0.015em",
  color: PALETTE.ink,
};

const headingStyleMobile: CSSProperties = {
  ...headingStyle,
  fontSize: 20,
};

const descStyle: CSSProperties = {
  fontSize: 14,
  color: PALETTE.ink3,
  lineHeight: 1.6,
  margin: "0 auto 22px",
  maxWidth: 440,
};

const descStyleMobile: CSSProperties = {
  ...descStyle,
  fontSize: 12.5,
};

const ctaBtnStyle: CSSProperties = {
  background: PALETTE.ink,
  color: "#ffffff",
  border: 0,
  padding: "11px 22px",
  borderRadius: 10,
  fontSize: 13.5,
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  boxShadow: "0 1px 2px rgba(14, 26, 26, 0.08)",
  fontFamily: "inherit",
};

const howSection: CSSProperties = {
  maxWidth: 640,
  margin: "24px auto 0",
  padding: "32px 24px 56px",
  borderTop: `1px solid ${PALETTE.lineSoft}`,
};

const howSectionMobile: CSSProperties = {
  ...howSection,
  padding: "24px 16px 40px",
};

const howLabelStyle: CSSProperties = {
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  fontWeight: 700,
  color: PALETTE.ink3,
  marginBottom: 18,
  textAlign: "center",
};

const howStep: CSSProperties = {
  display: "flex",
  gap: 18,
  padding: "14px 0",
};

const howStepMobile: CSSProperties = {
  ...howStep,
  padding: "12px 0",
  gap: 14,
};

const stepNum: CSSProperties = {
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: "50%",
  background: PALETTE.ink,
  color: "#ffffff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
  fontWeight: 700,
};

const stepNumMobile: CSSProperties = {
  ...stepNum,
  width: 26,
  height: 26,
  fontSize: 12,
};

const stepTitleStyle: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: PALETTE.ink,
};

const stepDescStyle: CSSProperties = {
  fontSize: 12.5,
  color: PALETTE.ink3,
  marginTop: 3,
  lineHeight: 1.55,
};

const vMarkStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 16,
  height: 16,
  borderRadius: "50%",
  background: PALETTE.vela,
  color: "#ffffff",
  fontSize: 10,
  fontWeight: 700,
};

const toastStyle: CSSProperties = {
  position: "fixed",
  bottom: 24,
  left: "50%",
  transform: "translateX(-50%)",
  background: PALETTE.ink,
  color: "#ffffff",
  padding: "10px 16px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  boxShadow: "0 8px 20px -4px rgba(14, 26, 26, 0.28)",
  zIndex: 1100,
};